/**
 * ROLE-BASED PROTECTION FOR AUTHENTICATED PAGES
 * =============================================
 * This wrapper is used after sign-in is already confirmed.
 *
 * Plain-English view:
 * - `RequireAuth` answers "is this person signed in?"
 * - `RequireRole` answers "does this signed-in person have the right job role?"
 *
 * For this compatibility pass we use it to keep quotation pages visible only to
 * finance/admin users, which now matches the backend rules more closely.
 */

import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/Auth/AuthContext";
import { hasRequiredRoleAccess } from "@/utils/swaRole";

type RequireRoleProps = {
  children: JSX.Element;
  requiredRoles: string[];
  title?: string;
  message?: string;
};

export default function RequireRole({
  children,
  requiredRoles,
  title = "Access restricted",
  message = "This area is currently limited to finance or admin users."
}: RequireRoleProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const allowed = hasRequiredRoleAccess(user?.userRoles, user?.userDetails, requiredRoles);
  if (allowed) {
    return children;
  }

  return (
    <div
      style={{
        minHeight: "calc(100vh - 140px)",
        display: "grid",
        placeItems: "center",
        padding: "24px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "560px",
          background: "white",
          border: "1px solid #e4e7ec",
          borderRadius: "18px",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
          padding: "28px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "34px", marginBottom: "12px" }}>🔒</div>
        <h1 style={{ margin: "0 0 10px", fontSize: "24px", color: "#101828" }}>{title}</h1>
        <p style={{ margin: "0 0 20px", color: "#475467", lineHeight: 1.6 }}>{message}</p>
        <button
          type="button"
          onClick={() => navigate("/")}
          style={{
            border: "none",
            borderRadius: "12px",
            background: "#175cd3",
            color: "white",
            padding: "10px 16px",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
}
